// Helpers de visibilité du catalogue Retail.
//
// Centralise la règle « type client effectif » + « customer à passer à la grille »
// utilisée par Catalog.tsx, RetailCatalogDetail.tsx, RetailCatalogSearchBar.tsx
// (et le bloc debug de Menu.tsx).
//
// Règle (cf. DocRetail/GRILLE_ECR_ECF.txt) :
//  - admin / N1 / N2 du customer courant → null    (= tout visible)
//  - override debug 'RET'/'ERE'/'INT'    → ce type + grille du customer courant
//  - commercial / interne (tout sauf user-client) → null (= catalogue COMPLET),
//        quel que soit le customer sélectionné et sa compatibilité retail.
//        (le bouton « Créer commande Retail » reste, lui, conditionné au customer :
//         voir checks dans CustomerDetail / Orders / Customers)
//        ⚠️ TEMPORAIRE (jusqu'à nouvel ordre) : si le flag
//        glob_const.retail_catalog_hidden_for_commercials est actif, ces commerciaux/
//        internes (non managers, non admin) reçoivent 'NONE' → catalogue Retail masqué.
//  - user-client (retailRole === 'client') :
//        - pas de customer ou grille refuse → 'NONE' (rien visible)
//        - sinon → type métier du customer + grille du customer
//
// customerForGrid : passé à isVisibleRetailFamille() / isRetailModelVisibleForCustomer().
// null si on est viewer total sans customer → grille désactivée ;
// sinon le customer courant pour filtrer les familles autorisées.

import { useMemo } from 'react'
import usePrefStore from '../stores/prefs'
import useRetailRoleForCustomer from './useRetailRoleForCustomer'
import useSales from './useSales'
import { canCreateRetailOrder } from '../libs/retail_rules'
import glob_const from '../libs/glob_const'
import type { Customer } from '../types'

export type RetailCustomerType = 'RET' | 'ERE' | 'INT' | 'NONE' | null

export interface RetailCatalogScope {
   /** Type client effectif (filtre `.ret`/`.ere` sur les modèles). */
   customerType: RetailCustomerType
   /** Customer à passer à la grille (null = grille désactivée = tout visible). */
   customerForGrid: Customer | null
}

/**
 * Calcul du scope catalogue Retail pour le customer courant (prefs.curCustomer).
 * Cf. en-tête de fichier pour la règle complète.
 */
export default function useRetailCatalogScope(): RetailCatalogScope {
   const { curCustomer, retailRole, isRetailManager } = usePrefStore()
   const { getCustomerById, getCustomerRetailType } = useSales()
   const roleForCurCustomer = useRetailRoleForCustomer(curCustomer)

   return useMemo<RetailCatalogScope>(() => {
      const customer = curCustomer ? getCustomerById(curCustomer) : null

      // Manager du customer courant ou admin global → tout visible
      if (roleForCurCustomer.isAdmin || roleForCurCustomer.isN1 || roleForCurCustomer.isN2) {
         return { customerType: null, customerForGrid: null }
      }
      // Surcharge debug d'un type client
      if (retailRole === 'RET' || retailRole === 'ERE' || retailRole === 'INT') {
         return { customerType: retailRole, customerForGrid: customer }
      }
      // Tout utilisateur connecté qui n'est PAS un user-client (commercial, interne, manager,
      // admin) voit normalement le catalogue Retail COMPLET, quel que soit le customer
      // sélectionné. La création de commande reste, elle, conditionnée au customer sélectionné
      // (checks séparés : CustomerDetail / Orders / Customers).
      if (retailRole !== 'client') {
         // ── TEMPORAIRE (jusqu'à nouvel ordre) ──────────────────────────────────
         // Masquage du catalogue Retail pour les COMMERCIAUX / internes : rôle non
         // retail (ni client, ni admin, ni manager N1/N2 → isRetailManager). Les
         // managers et admins conservent l'accès. Piloté par le flag
         // glob_const.retail_catalog_hidden_for_commercials. Rétablir = flag à false.
         // 'NONE' → isRetailModelVisibleForCustomer masque tous les modèles retail
         // partout (Catalog, RetailCatalogDetail, RetailCatalogSearchBar, useCatalogScopeFlags).
         if (glob_const.retail_catalog_hidden_for_commercials
            && retailRole !== 'admin' && !isRetailManager) {
            return { customerType: 'NONE', customerForGrid: null }
         }
         // ───────────────────────────────────────────────────────────────────────
         return { customerType: null, customerForGrid: null }
      }
      // User-client : filtrage selon SON propre profil retail.
      //  - pas de customer ou grille refuse → 'NONE' (rien visible)
      //  - sinon → type métier du customer + grille du customer
      if (!customer) {
         return { customerType: 'NONE', customerForGrid: null }
      }
      if (!canCreateRetailOrder(customer)) {
         return { customerType: 'NONE', customerForGrid: null }
      }
      return { customerType: getCustomerRetailType(customer) ?? 'NONE', customerForGrid: customer }
   }, [curCustomer, retailRole, isRetailManager, getCustomerById, getCustomerRetailType, roleForCurCustomer])
}
